import { attachmentsQueryOptions } from '~/modules/attachments/query/options';
import { labelsQueryOptions } from '~/modules/labels/query/options';
import type { UserMenuItem } from '~/modules/me/types';
import { membersQueryOptions } from '~/modules/memberships/query/options';
import { organizationQueryOptions } from '~/modules/organizations/query';
import { tasksBoardQueryOptions, tasksTableQueryOptions } from '~/modules/tasks/query/options';
import { workspaceQueryOptions } from '~/modules/workspaces/query';

// This function returns queries that need to be prefetched based on the entity of the item.
// It is used to prefetch data for each unarchived item in user menu if offlineAccess is enabled, allowing the app to fetch necessary data while offline.
export const queriesToMap = (item: UserMenuItem) => {
  const orgIdOrSlug = item.organizationId ?? item.id;

  // Switch statement to handle different entity types.
  // The entity type will decide which queries should be returned for prefetching.
  switch (item.entity) {
    case 'organization':
      // As example for 'organization' we return the following queries:
      // - queryOptions to fetch the organization itself
      // - queryOptions to fetch members of the organization
      // - queryOptions to fetch attachments related to the organization
      return [
        organizationQueryOptions(item.slug),
        membersQueryOptions({
          idOrSlug: item.slug,
          orgIdOrSlug,
          entityType: item.entity,
        }),
        attachmentsQueryOptions({ orgIdOrSlug }),
      ];
    case 'workspace': {
      const projects = item.submenu?.filter(({ membership }) => !membership.archived) ?? [];
      const queryOptions = {
        orgIdOrSlug,
        projectId: projects.map(({ id }) => id).join('_'),
      };
      return [workspaceQueryOptions(item.slug, orgIdOrSlug), tasksTableQueryOptions(queryOptions), labelsQueryOptions(queryOptions)];
    }
    case 'project':
      return [
        tasksBoardQueryOptions({
          projectId: item.id,
          orgIdOrSlug,
        }),
        membersQueryOptions({
          idOrSlug: item.slug,
          orgIdOrSlug,
          entityType: item.entity,
        }),
      ];

    // In case we don't have a matching entity, return an empty array or add default set of queries
    default:
      return [];
  }
};
